/**
 * Adapter shim — Anvil's `src/api.ts` surface re-implemented over Foundry's
 * `window.crucible.invoke`. See docs/anvil-foundry-design-tab-tdd.md §5.
 *
 * The ported Design store/components import these functions in the same shape
 * as Anvil's `api` object; the only difference is that Anvil's leading
 * `projectPath` argument is replaced by `gameId` (the renderer never knows a
 * filesystem path — the main process resolves `<gamePath>/design/`).
 *
 * No Electron / Tauri imports — this file talks only to the typed
 * `window.crucible` bridge, so it satisfies crucible-ui's no-electron lint.
 *
 * Phase 0 implements the project + section subset; later phases fill in
 * screens / flow / tunables / lenses / AI (their channels already exist in
 * the channel map).
 */
import type {
  BuildUpdateStatus,
  DependencyGraph,
  DesignAiChatRequest,
  DesignChangeSummary,
  DesignExportFoundryPackageResult,
  DesignProfile,
  DesignProject,
  DesignReconcileResult,
  DesignReconcileStatus,
  DocKind,
  Lens,
  LensFull,
  ReferenceMeta,
  Screen,
  SectionMeta,
} from '@volley/crucible-ipc-types';

// ---- Project ----

export function openProject(gameId: string): Promise<DesignProject> {
  return window.crucible.invoke('design:openProject', { gameId });
}

export function writeProjectMeta(
  gameId: string,
  patch: {
    pillars?: string[];
    sectionItemMode?: { id: string; itemMode: boolean };
    profile?: Partial<DesignProfile>;
    name?: string;
  },
): Promise<DesignProject> {
  return window.crucible.invoke('design:writeProjectMeta', { gameId, patch });
}

/** Toggle a section between the item-based editor and freeform-document mode. */
export function setSectionItemMode(
  gameId: string,
  sectionId: string,
  itemMode: boolean,
): Promise<DesignProject> {
  return writeProjectMeta(gameId, { sectionItemMode: { id: sectionId, itemMode } });
}

/** Merge a partial game profile (genre / players / input / art style). */
export function writeProjectProfile(
  gameId: string,
  profile: Partial<DesignProfile>,
): Promise<DesignProject> {
  return writeProjectMeta(gameId, { profile });
}

/** Rename the design (project.json name + dashboard displayName). */
export function renameProject(gameId: string, name: string): Promise<DesignProject> {
  return writeProjectMeta(gameId, { name });
}

/** Suggest a game name from a concept (reuses the create-flow handler). */
export function suggestGameName(conceptText: string): Promise<{ displayName: string }> {
  return window.crucible.invoke('game:suggest-name', { conceptText });
}

// ---- Sections ----

export function readSection(gameId: string, filename: string): Promise<string> {
  return window.crucible.invoke('design:readSection', { gameId, filename });
}

/** Last-built (snapshot) body for a section — for item-level "edited since
 *  last build" pips. Resolves null when there's no snapshot yet. */
export function getBuildSnapshotSection(gameId: string, filename: string): Promise<string | null> {
  return window.crucible.invoke('design:getBuildSnapshotSection', { gameId, filename });
}

export function writeSection(gameId: string, filename: string, body: string): Promise<void> {
  return window.crucible.invoke('design:writeSection', { gameId, filename, body });
}

export function addSection(gameId: string, title: string, doc: DocKind): Promise<SectionMeta> {
  return window.crucible.invoke('design:addSection', { gameId, title, doc });
}

export function deleteSection(gameId: string, sectionId: string): Promise<void> {
  return window.crucible.invoke('design:deleteSection', { gameId, sectionId });
}

// ---- Screens ----

export function listScreens(gameId: string): Promise<Screen[]> {
  return window.crucible.invoke('design:listScreens', { gameId });
}

export function readScreen(gameId: string, screenId: string): Promise<Screen> {
  return window.crucible.invoke('design:readScreen', { gameId, screenId });
}

export function writeScreen(gameId: string, screen: Screen, svg: string): Promise<Screen> {
  return window.crucible.invoke('design:writeScreen', { gameId, screen, svg });
}

export function deleteScreen(gameId: string, screenId: string): Promise<void> {
  return window.crucible.invoke('design:deleteScreen', { gameId, screenId });
}

// ---- Graph ----

export function readGraph(gameId: string): Promise<DependencyGraph> {
  return window.crucible.invoke('design:readGraph', { gameId });
}

export function writeGraph(gameId: string, graph: DependencyGraph): Promise<void> {
  return window.crucible.invoke('design:writeGraph', { gameId, graph });
}

// ---- References ----

export function listReferences(gameId: string): Promise<ReferenceMeta[]> {
  return window.crucible.invoke('design:listReferences', { gameId });
}

export function readReference(gameId: string, filename: string): Promise<string> {
  return window.crucible.invoke('design:readReference', { gameId, filename });
}

// ---- Design references (bundled Weekend docs, folder-driven, optional) ----

/** List the bundled Weekend design-reference docs (every `*.md` in the
 *  references folder — add/edit/remove freely). */
export function listReferenceDocs(): Promise<ReferenceMeta[]> {
  return window.crucible.invoke('design:listReferenceDocs');
}

/** Read one reference doc's body (frontmatter stripped) for prompt context. */
export function readReferenceDoc(filename: string): Promise<string> {
  return window.crucible.invoke('design:readReferenceDoc', { filename });
}

// ---- Lenses (global, not per-game) ----

export function listLenses(): Promise<Lens[]> {
  return window.crucible.invoke('design:listLenses');
}

export function readLens(id: string, kind: 'agent' | 'skill'): Promise<LensFull> {
  return window.crucible.invoke('design:readLens', { id, kind });
}

// ---- AI ----

// ---- Build handoff ----

/** Generate the Foundry build package into a committed `design/<subdir>/`.
 *  First send (`mode: 'full'`) regenerates the full spec; subsequent sends
 *  (`mode: 'update'`) ship a deterministic changelog instead. Returns the
 *  subdir, phase plan, the "Game Plan" markdown shown in the embedded Build
 *  Plan surface, the mode, seeded plan metadata, and (for updates) a
 *  structured change summary. */
export function exportFoundryPackage(gameId: string): Promise<DesignExportFoundryPackageResult> {
  return window.crucible.invoke('design:exportFoundryPackage', { gameId });
}

/** Has this design been sent to build before, and does it have unsent changes?
 *  Drives the design tab's "Send to build" → "Send Build Updates" button. */
export function getBuildUpdateStatus(gameId: string): Promise<BuildUpdateStatus> {
  return window.crucible.invoke('design:getBuildUpdateStatus', { gameId });
}

/** Advance the "last sent" snapshot. Called only when the user CONFIRMS the
 *  send (final button click) — preparing/dismissing the dialog must not move
 *  it, so the button keeps reflecting the real pending changes. */
export function commitBuildSnapshot(gameId: string, subdir?: string): Promise<void> {
  return window.crucible.invoke('design:commitBuildSnapshot', { gameId, subdir });
}

// ---- Bi-directional sync (chat → design) ----

/** New chat activity since the design docs were last reconciled from chat.
 *  Drives the "Sync design from chat (N)" control. */
export function getReconcileStatus(gameId: string): Promise<DesignReconcileStatus> {
  return window.crucible.invoke('design:getReconcileStatus', { gameId });
}

/** AI pass: propose design-section edits that reflect recent chat changes. */
export function reconcileFromChat(gameId: string): Promise<DesignReconcileResult> {
  return window.crucible.invoke('design:reconcileFromChat', { gameId });
}

/** Write the accepted section edits + advance the reconcile marker (and the
 *  build snapshot when sections were written). Empty `sections` = dismiss. */
export function applyReconcile(
  gameId: string,
  sections: { filename: string; body: string; original?: string }[],
  chatCount: number,
): Promise<void> {
  return window.crucible.invoke('design:applyReconcile', { gameId, sections, chatCount });
}

/** Cancel an in-flight build-package export. */
export function cancelExport(gameId: string): Promise<void> {
  return window.crucible.invoke('design:cancelExport', { gameId });
}

/** One-shot chat (no streaming). */
export function aiChat(
  req: Omit<DesignAiChatRequest, 'gameId'> & { gameId: string },
): Promise<{ text: string; usage?: unknown }> {
  return window.crucible.invoke('design:aiChat', req);
}

export interface AiStreamCallbacks {
  onDelta?: (text: string, accumulated: number) => void;
  onStatus?: (status: string) => void;
}

export interface AiStreamResult {
  text: string;
  usage?: unknown;
  cancelled?: boolean;
}

/**
 * Streaming chat. Re-creates Anvil's `aiChatStream(args, { onDelta, onStatus })`.
 *
 * ⚠️ Ordering invariant (TDD §5): the renderer MINTS the `requestId` and
 * SUBSCRIBES to `design:aiStream:chunk` BEFORE invoking `design:aiStreamStart`.
 * Push chunks are ephemeral `webContents.send` (no buffering), so any chunk
 * emitted before `.on()` is registered is lost forever. In cloud mode the main
 * process resolves the whole call to one `done` chunk *during* the invoke — so
 * subscribing after the invoke would be a guaranteed miss, not a race. Minting
 * the id client-side lets us register the filtered listener first.
 *
 * Returns a `{ cancel }` handle plus the settling promise.
 */
export function aiChatStream(
  req: Omit<DesignAiChatRequest, 'gameId'> & { gameId: string },
  cb: AiStreamCallbacks = {},
): { requestId: string; cancel: () => void; done: Promise<AiStreamResult> } {
  const requestId =
    globalThis.crypto?.randomUUID?.() ?? `req-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

  let settled = false;
  const done = new Promise<AiStreamResult>((resolve, reject) => {
    let acc = '';
    // Register the filtered listener BEFORE kickoff.
    const off = window.crucible.on('design:aiStream:chunk', (c) => {
      if (c.requestId !== requestId) return;
      if (c.type === 'delta') {
        acc += c.text ?? '';
        cb.onDelta?.(c.text ?? '', c.accumulated ?? acc.length);
      } else if (c.type === 'status' && c.status === 'cancelled') {
        // 'cancelled' is the ONE terminal status — settle + unsubscribe, else
        // the promise hangs and the listener leaks (TDD §5).
        settled = true;
        off();
        resolve({ text: acc, cancelled: true });
      } else if (c.type === 'status') {
        cb.onStatus?.(c.status ?? '');
      } else if (c.type === 'done') {
        settled = true;
        off();
        resolve({ text: c.text ?? acc, usage: c.usage });
      } else if (c.type === 'error') {
        settled = true;
        off();
        reject(new Error(c.error ?? 'stream error'));
      }
    });

    // Kick off only after the listener exists. If the invoke itself rejects,
    // tear the listener down.
    window.crucible.invoke('design:aiStreamStart', { ...req, requestId }).catch((e: unknown) => {
      if (settled) return;
      settled = true;
      off();
      reject(e instanceof Error ? e : new Error(String(e)));
    });
  });

  const cancel = (): void => {
    if (settled) return;
    void window.crucible.invoke('design:aiStreamCancel', { requestId });
  };

  return { requestId, cancel, done };
}
